// src/components/GameScreen.js
import React, { useState, useEffect } from 'react';
import { Heart, Star, BookOpen } from 'lucide-react';

const GameScreen = ({ 
  currentQuestion, 
  questions, 
  lives, 
  score, 
  selectedAnswer, 
  showResult, 
  onAnswerSelect,
  getCatSize,
  levelTitle,
  levelEmoji
}) => {
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [correctIndex, setCorrectIndex] = useState(0);
  const [clickedIndex, setClickedIndex] = useState(null);
  
  const currentQ = questions[currentQuestion];
  const catInfo = getCatSize();
  const progress = (score / questions.length) * 100;
  
  // Varsayılan değerler
  const displayTitle = levelTitle || "AyseLady";
  const displayEmoji = levelEmoji || "📚";

  // Her yeni soru geldiğinde seçenekleri karıştır
  useEffect(() => {
    if (currentQ) {
      const correctAnswer = currentQ.options[currentQ.correct];
      const shuffled = [...currentQ.options].sort(() => Math.random() - 0.5);
      const newCorrectIndex = shuffled.indexOf(correctAnswer);
      
      setShuffledOptions(shuffled);
      setCorrectIndex(newCorrectIndex);
      setClickedIndex(null);
    }
  }, [currentQuestion, currentQ]);

  // Cevap seçimi
  const handleAnswerClick = (index) => {
    if (!showResult) {
      setClickedIndex(index);
      const isCorrect = index === correctIndex;
      onAnswerSelect(isCorrect);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-400 to-purple-500 p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-full p-2 mr-2">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div className="text-center">
              <h1 className="text-lg font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
                {displayEmoji} {displayTitle}
              </h1>
              <div className="text-xs text-gray-500">AyseLady</div>
            </div>
          </div>
          
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-1">
              {[...Array(3)].map((_, i) => (
                <Heart 
                  key={i} 
                  className={`w-6 h-6 ${i < lives ? 'text-red-500 fill-red-500' : 'text-gray-300'}`} 
                />
              ))}
            </div>
            <div className="text-lg font-bold text-gray-700">
              {score}/{questions.length} <Star className="inline w-5 h-5 text-yellow-500 fill-yellow-500" />
            </div>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div 
              className="bg-gradient-to-r from-green-400 to-blue-500 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="text-center text-sm text-gray-600">
            Soru {currentQuestion + 1} / {questions.length}
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <div className="text-center mb-4">
            <div className="text-sm text-gray-500 mb-2">
              {currentQ.type === 'en-tr' ? 'İngilizce → Türkçe' : 'Türkçe → İngilizce'}
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {currentQ.question}
            </h2>
          </div>

          <div className="space-y-3">
            {shuffledOptions.map((option, index) => (
              <button
                key={`${currentQuestion}-${index}`}
                onClick={() => handleAnswerClick(index)}
                disabled={showResult}
                className={`w-full p-4 rounded-lg border-2 font-semibold transition-all duration-200 ${
                  showResult
                    ? index === correctIndex
                      ? 'bg-green-100 border-green-500 text-green-700'
                      : index === clickedIndex
                      ? 'bg-red-100 border-red-500 text-red-700'
                      : 'bg-gray-100 border-gray-300 text-gray-600'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2'
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          {showResult && (
            <div className="mt-4 text-center">
              <div className={`text-lg font-bold ${
                clickedIndex === correctIndex ? 'text-green-600' : 'text-red-600'
              }`}>
                {clickedIndex === correctIndex ? '✓ Doğru!' : '✗ Yanlış!'}
              </div>
              {clickedIndex !== correctIndex && (
                <div className="text-sm text-gray-600 mt-1">
                  Doğru cevap: {shuffledOptions[correctIndex]}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cat Display */}
        <div className="bg-white rounded-xl shadow-lg p-4 text-center">
          <div className="text-lg font-semibold text-gray-700 mb-2">
            {catInfo.stage}
          </div>
          <div 
            className="transition-all duration-500 mx-auto mb-2"
            style={{ 
              fontSize: `${catInfo.size}px`,
              lineHeight: 1
            }}
          >
            🐱
          </div>
          <div className="text-sm text-gray-500">
            {Math.round(progress)}% büyüdü
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div 
              className="bg-gradient-to-r from-pink-400 to-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameScreen;